import { createReducer, type Draft } from '@reduxjs/toolkit';
import { fitmentSearchEnabled } from 'BuildArtifacts/featureFlags';

import {
  discardVehicleFieldsRequested,
  forceVehicleSelectionLater,
  ignoreInexactFields,
  removalFromGarageRequested,
  replaceVehicleValue,
  setGarageRequested,
  vehicleDiscardingRequested,
  vehicleSelected,
  vehicleSpecificationNeeded,
} from 'Core/actions/fitmentSearch/index';
import { garageCleanupRequested } from 'Core/actions/fitmentSearch/vehicle';
import { resetRequest } from 'Core/actions/request';
import { fitmentSearchFieldsSelector } from 'Core/selectors/fitmentSearch/fitmentSearch';
import type { ReduxStateType, ReduxStoreType } from 'Core/store';
import { logRuntimeError } from 'Diagnostics/diagnostics';
import { Vehicle, VehicleCollection } from 'Models/index';
import requestConfig from 'Models/uiConfig/requestConfig';
import type { VehicleValue } from 'Models/vehicle';
import { getCustomerGarage } from 'Modules/serverApi/index';
import {
  getForceVehicleSelectionLater,
  getLoggedInState,
  loadGarageData,
  loadSelectedVehicle,
  setLoggedInState,
} from 'Utils/userStorage';

const maxGarageSize = 20;
type Storage = {
  garage: VehicleCollection;
  selectedVehicle: string | null;
  forceVehicleSelectionLater: boolean;
};

const emptyState: Storage = {
  garage: VehicleCollection.empty,
  selectedVehicle: null,
  forceVehicleSelectionLater: false,
};

const initState = createInitialState();

export default createReducer(initState, (builder) =>
  builder
    .addCase(vehicleSelected, (draft, { payload }) => {
      if (draft.selectedVehicle !== payload.key) {
        replaceSelectedVehicleAndAddToGarage(draft, () => payload);
      }
    })
    .addCase(vehicleDiscardingRequested, (draft) => {
      setSelectedVehicle(draft, null);
    })
    .addCase(discardVehicleFieldsRequested, (draft, { meta: { fitmentFields, fieldsToDiscard } }) => {
      const selectedVehicle = draft.garage.findByKey(draft.selectedVehicle);
      if (
        !selectedVehicle?.some(
          (value) => Vehicle.isUsualTerm(value.field) && fieldsToDiscard.includes(value.field),
        )
      ) {
        return;
      }

      const newVehicle = new Vehicle(
        selectedVehicle.filter((v) => !Vehicle.isUsualTerm(v.field) || !fieldsToDiscard.includes(v.field)),
        fitmentFields,
      );

      if (newVehicle.notNull) {
        draft.garage = draft.garage.replace(selectedVehicle.key, newVehicle);
        setSelectedVehicle(draft, newVehicle);
      } else {
        draft.garage = draft.garage.remove(selectedVehicle);
        setSelectedVehicle(draft, null);
      }
    })
    .addCase(vehicleSpecificationNeeded, (draft, { payload: fieldsToSpecify }) => {
      replaceSelectedVehicleAndAddToGarage(draft, (vehicle) =>
        vehicle.concat(
          fieldsToSpecify
            .filter((field) => !vehicle.some((v) => v.field === field))
            .map<VehicleValue>((field) => ({ field, term: '__inexact', value: '__inexact' })),
        ),
      );
    })
    .addCase(ignoreInexactFields, (draft) => {
      replaceSelectedVehicleAndAddToGarage(draft, (vehicle) =>
        vehicle.mapToVehicle((v) =>
          Vehicle.isUsualTerm(v.field)
            ? { ...v, term: ignoreInexact(v.term), value: ignoreInexact(v.value) }
            : v,
        ),
      );
    })
    .addCase(garageCleanupRequested, () => emptyState)
    .addCase(setGarageRequested, (draft, { payload: { vehicles, selectedVehicle } }) => {
      let garage = vehicles;
      for (const vehicle of vehicles.copy()) {
        const updatedVehicle = preserveVehicleData(vehicle, draft.garage.find(vehicle.equals));
        garage = garage.replace(vehicle.key, updatedVehicle);
      }
      const currentVehicle = draft.garage.findByKey(draft.selectedVehicle) ?? Vehicle.null;
      let selectedKey = selectedVehicle ? null : (garage.find(currentVehicle.equals)?.key ?? null);

      if (selectedVehicle?.notNull) {
        const incomingVehicle = preserveVehicleData(
          selectedVehicle,
          draft.garage.find(selectedVehicle.equals),
        );
        const [savedVehicle, resolvedVehicle] = resolveVehicle(garage, incomingVehicle);
        const vehicle = incomingVehicle.equals(resolvedVehicle)
          ? incomingVehicle
          : new Vehicle(
              [
                ...incomingVehicle.filter((value) => !resolvedVehicle._fields.includes(value.field)),
                ...resolvedVehicle.fitmentValues,
              ],
              resolvedVehicle._fields,
            );
        garage = savedVehicle ? garage.replace(savedVehicle.key, vehicle) : garage.add(vehicle);
        selectedKey = vehicle.key;
      }

      draft.garage = getLimitedGarage(garage, selectedKey);
      setSelectedVehicle(draft, draft.garage.findByKey(selectedKey) ?? null);
    })
    .addCase(removalFromGarageRequested, (draft, { payload }) => {
      const selectedVehicle = draft.garage.findByKey(draft.selectedVehicle);
      draft.garage = draft.garage.remove(payload);
      if (selectedVehicle?.equalsBySelection(payload)) {
        setSelectedVehicle(draft, null);
      }
    })
    .addCase(replaceVehicleValue, (draft, { meta: { isolatedKey } }) => {
      if (!isolatedKey) {
        setSelectedVehicle(draft, null);
      }
    })
    .addCase(forceVehicleSelectionLater, (draft) => {
      draft.forceVehicleSelectionLater = true;
    })
    .addCase(resetRequest, (draft, { payload: { discardVehicle } }) => {
      if (discardVehicle) {
        setSelectedVehicle(draft, null);
      }
    }),
);

function preserveVehicleData(vehicle: Vehicle, savedVehicle: Vehicle | undefined): Vehicle {
  if (!savedVehicle || savedVehicle === vehicle) {
    return vehicle;
  }

  return vehicle.mapToVehicle((value) => {
    const savedValue = savedVehicle.fitmentValues.find(
      (saved) => saved.field === value.field && saved.term === value.term,
    );
    return {
      ...value,
      payload: value.payload ?? savedValue?.payload,
      imageUrl: value.imageUrl ?? savedValue?.imageUrl,
    };
  });
}

function createInitialState(): Storage {
  const selectedVehicle = loadSelectedVehicle();
  const garage = loadGarageData();
  const selectedVehicleKey = selectedVehicle.notNull ? selectedVehicle.key : null;
  const vehicles = selectedVehicleKey ? garage.add(selectedVehicle) : garage;

  return {
    ...emptyState,
    garage: getLimitedGarage(vehicles, selectedVehicleKey),
    selectedVehicle: selectedVehicleKey,
    forceVehicleSelectionLater: getForceVehicleSelectionLater() ?? false,
  };
}

function getLimitedGarage(garage: VehicleCollection, selectedVehicleKey: string | null): VehicleCollection {
  if (garage.size <= maxGarageSize) {
    return garage;
  }

  const newestVehicles = garage.slice(-maxGarageSize);
  const selectedVehicle = garage.findByKey(selectedVehicleKey);
  if (selectedVehicle && !newestVehicles.includes(selectedVehicle)) {
    newestVehicles[0] = selectedVehicle;
  }

  return new VehicleCollection(newestVehicles);
}

function setSelectedVehicle(state: Draft<Storage>, vehicle: Vehicle | null): void {
  state.selectedVehicle = vehicle?.key ?? null;
}

function ignoreInexact(v: string) {
  return v === '__inexact' ? '__ignored' : v;
}

const fitmentSearchSelector = (state: ReduxStateType) => state.fitmentSearch;

export function selectedVehicleSelector(state: ReduxStateType): Vehicle {
  const { selectedVehicle, garage } = fitmentSearchSelector(state).storage;
  return garage.findByKey(selectedVehicle) ?? Vehicle.null;
}

export function isVehicleSelectedSelector(state: ReduxStateType): boolean {
  return selectedVehicleSelector(state).notNull;
}

export function vehicleInexactSelector(state: ReduxStateType): boolean {
  return selectedVehicleSelector(state).fitmentValues.some(
    (v) => v.term === '__inexact' || v.value === '__inexact',
  );
}

export function garageDataSelector(state: ReduxStateType): VehicleCollection {
  return fitmentSearchSelector(state).storage.garage;
}

export function forceVehicleSelectionLaterSelector(state: ReduxStateType): boolean {
  return fitmentSearchSelector(state).storage.forceVehicleSelectionLater;
}

function replaceSelectedVehicleAndAddToGarage(
  draft: Draft<Storage>,
  transform: (selectedVehicle: Vehicle) => Vehicle,
) {
  const { garage } = draft;
  const newVehicle = transform(garage.findByKey(draft.selectedVehicle) ?? Vehicle.null);
  if (!newVehicle.notNull) {
    return;
  }
  const [savedVehicle, resolvedVehicle] = resolveVehicle(garage, newVehicle);
  if (savedVehicle === resolvedVehicle) {
    setSelectedVehicle(draft, resolvedVehicle);
    return;
  }

  const vehicle = savedVehicle
    ? new Vehicle(
        [
          ...savedVehicle.filter((value) => !resolvedVehicle.some((next) => next.field === value.field)),
          ...resolvedVehicle.serializable,
        ],
        resolvedVehicle._fields,
      )
    : newVehicle;
  setSelectedVehicle(draft, vehicle);
  draft.garage = savedVehicle
    ? garage.replace(savedVehicle.key, vehicle)
    : getLimitedGarage(garage.add(vehicle), draft.selectedVehicle);
}

function resolveVehicle(
  garage: Pick<VehicleCollection, 'find' | 'copy'>,
  newVehicle: Vehicle,
): [Vehicle | undefined, Vehicle] {
  const savedVehicle =
    garage.find((vehicle) => vehicle.equals(newVehicle)) ??
    garage.find((vehicle) => vehicle.isMoreSpecificThan(newVehicle));
  if (savedVehicle) {
    return [savedVehicle, savedVehicle];
  }

  const lessSpecificVehicle = garage.find((vehicle) => newVehicle.isMoreSpecificThan(vehicle));
  if (lessSpecificVehicle) {
    return [lessSpecificVehicle, newVehicle];
  }

  for (const vehicle of garage.copy()) {
    const mergedVehicle = vehicle.merge(newVehicle);
    if (mergedVehicle) {
      return [vehicle, mergedVehicle];
    }
  }

  return [undefined, newVehicle];
}

export function initializeVehicleStorage(store: ReduxStoreType): void {
  if (!fitmentSearchEnabled) {
    return;
  }

  const restoredGarage = garageDataSelector(store.getState());
  const syncCustomerGarage = async (): Promise<void> => {
    try {
      const customer = await requestConfig.getCustomerAsync?.();
      if (!customer?.id && requestConfig.getCustomerAsync && window.Convermax.isLoggedIn !== null) {
        setLoggedInState(false);
      }

      const vehicleData = customer?.id ? await getCustomerGarage(String(customer.id)) : undefined;
      if (!vehicleData) {
        return;
      }

      const state = store.getState();
      let garage = garageDataSelector(state);
      if (vehicleData.Garage && getLoggedInState()) {
        garage = new VehicleCollection(garage.copy().filter((vehicle) => !restoredGarage.includes(vehicle)));
      }
      const customerVehicles = new VehicleCollection(
        (vehicleData.Garage ?? []).map((record) => new Vehicle(record, fitmentSearchFieldsSelector(state))),
      );
      const useCustomerGarage =
        !!vehicleData.Garage && !garage.find((vehicle) => !customerVehicles.includes(vehicle));

      setLoggedInState(useCustomerGarage);

      let vehicles = useCustomerGarage ? customerVehicles : garage.concat(customerVehicles);
      const selectedVehicle = selectedVehicleSelector(state);
      if (selectedVehicle.notNull && !vehicles.includes(selectedVehicle)) {
        vehicles = vehicles.add(selectedVehicle);
      }

      store.dispatch(setGarageRequested({ vehicles }, { onInit: useCustomerGarage }));
    } catch (error) {
      console.error("Failed to sync customer's garage", error);
      logRuntimeError({ source: 'garage_sync', error });
    }
  };

  void syncCustomerGarage();
}
